import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  ColumnLayout,
  Box,
  Badge,
  ProgressBar,
  Button,
  SpaceBetween,
  ExpandableSection
} from '@cloudscape-design/components';
import { blissApi } from './blissApi.js';

export default function ProcessingSection({ jobRunId, jobIdentifier, onJobCompleted }) {
  const [status, setStatus] = useState('RUNNING');
  const [startTime, setStartTime] = useState(null);
  const [finishTime, setFinishTime] = useState(null);
  const [executionTime, setExecutionTime] = useState(0);
  const [logs, setLogs] = useState('Waiting for logs...');
  const [progress, setProgress] = useState(0);
  const [totalLeads, setTotalLeads] = useState(0);
  const [processedLeads, setProcessedLeads] = useState(0);
  const [outputFile, setOutputFile] = useState(null);

  // Track whether we've already fired onJobCompleted for this job
  const completedRef = useRef(false);

  // Reset all state when jobRunId changes (new job started)
  useEffect(() => {
    setStatus('RUNNING');
    setStartTime(null);
    setFinishTime(null);
    setExecutionTime(0);
    setLogs('Waiting for logs...');
    setProgress(0);
    setTotalLeads(0);
    setProcessedLeads(0);
    setOutputFile(null);
    completedRef.current = false;
  }, [jobRunId]);

  useEffect(() => {
    if (!jobRunId) return;

    const statusInterval = setInterval(async () => {
      try {
        const data = await blissApi.getJobStatus(jobRunId);
        setStatus(data.status);
        setStartTime(data.started_on);
        setFinishTime(data.completed_on);
        setExecutionTime(data.execution_time);

        if (data.output_file) {
          setOutputFile(data.output_file);
        }

        if (data.status === 'SUCCEEDED' || data.status === 'FAILED' || data.status === 'STOPPED') {
          clearInterval(statusInterval);
          clearInterval(logsInterval);
          // Only fire once per job
          if (!completedRef.current) {
            completedRef.current = true;
            if (onJobCompleted) onJobCompleted();
          }
        }
      } catch (err) {
        console.error('Error fetching status:', err);
      }
    }, 3000);

    const logsInterval = setInterval(async () => {
      try {
        const data = await blissApi.getJobLogs(jobIdentifier || jobRunId);
        setLogs(data.logs || 'No logs available yet...');

        // Parse progress from logs
        const logText = data.logs || '';

        // Extract total leads
        const totalMatch = logText.match(/Processing (\d+) leads/);
        if (totalMatch) {
          setTotalLeads(parseInt(totalMatch[1]));
        }

        // Extract processed leads
        const completedMatches = logText.match(/✅ COMPLETED LEAD (\d+)\/(\d+)/g);
        if (completedMatches) {
          const lastMatch = completedMatches[completedMatches.length - 1];
          const nums = lastMatch.match(/(\d+)\/(\d+)/);
          if (nums) {
            const processed = parseInt(nums[1]);
            const total = parseInt(nums[2]);
            setProcessedLeads(processed);
            setTotalLeads(total);
            setProgress((processed / total) * 100);
          }
        }
      } catch (err) {
        console.error('Error fetching logs:', err);
      }
    }, 2000);

    return () => {
      clearInterval(statusInterval);
      clearInterval(logsInterval);
    };
  }, [jobRunId]); // Only depend on jobRunId, not onJobCompleted

  const getStatusBadge = () => {
    const statusMap = {
      'RUNNING': { color: 'blue', text: 'Running' },
      'SUCCEEDED': { color: 'green', text: 'Succeeded' },
      'FAILED': { color: 'red', text: 'Failed' },
      'STOPPED': { color: 'grey', text: 'Stopped' },
      'TIMEOUT': { color: 'red', text: 'Timeout' }
    };
    const config = statusMap[status] || { color: 'grey', text: status };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const handleDownload = async () => {
    try {
      await blissApi.downloadJobOutput(jobRunId);
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Container
      header={
        <Header variant="h2">
          ⚙️ Processing Status
        </Header>
      }
    >
      <SpaceBetween size="l">
        <ColumnLayout columns={4} variant="text-grid">
          <div>
            <Box variant="awsui-key-label">Status</Box>
            <div>{getStatusBadge()}</div>
          </div>
          <div>
            <Box variant="awsui-key-label">Started</Box>
            <div>{formatTime(startTime)}</div>
          </div>
          <div>
            <Box variant="awsui-key-label">Finished</Box>
            <div>{formatTime(finishTime)}</div>
          </div>
          <div>
            <Box variant="awsui-key-label">Execution Time</Box>
            <div>{formatDuration(executionTime)}</div>
          </div>
        </ColumnLayout>

        {totalLeads > 0 && (
          <SpaceBetween size="s">
            <ColumnLayout columns={3} variant="text-grid">
              <div>
                <Box variant="awsui-key-label">Total Leads</Box>
                <Box fontSize="heading-l">{totalLeads}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label" color="text-status-success">Processed</Box>
                <Box fontSize="heading-l" color="text-status-success">{processedLeads}</Box>
              </div>
              <div>
                <Box variant="awsui-key-label" color="text-status-info">Remaining</Box>
                <Box fontSize="heading-l" color="text-status-info">{totalLeads - processedLeads}</Box>
              </div>
            </ColumnLayout>

            <ProgressBar
              value={progress}
              label={`Processing lead ${processedLeads} of ${totalLeads}`}
              description={`${Math.round(progress)}% complete`}
              status={status === 'SUCCEEDED' ? 'success' : status === 'FAILED' ? 'error' : 'in-progress'}
            />
          </SpaceBetween>
        )}

        <ExpandableSection
          headerText="💻 Detailed Processing Logs"
          defaultExpanded={true}
        >
          <Box padding="s">
            <pre className="bliss-logs-container">
              {logs}
            </pre>
          </Box>
        </ExpandableSection>

        {status === 'SUCCEEDED' && outputFile && (
          <Button
            variant="primary"
            iconName="download"
            onClick={handleDownload}
          >
            Download Enriched CSV
          </Button>
        )}
      </SpaceBetween>
    </Container>
  );
}
